<template>
  <div class="user-data-container">
    <v-progress-circular
      v-if="loading"
      indeterminate
      color="primary"
      class="loading-spinner" />
    
    <div v-if="!loading" class="profile-layout">
      <!-- Profile Header Card -->
      <div class="profile-header-card">
        <div class="avatar-section">
          <v-avatar size="80" class="profile-avatar">
            <span class="avatar-text">
              {{ patient.firstName.charAt(0) + patient.lastName.charAt(0) }}
            </span>
          </v-avatar>
        </div>
        <h2 class="profile-name">
          {{ patient.firstName + ' ' + patient.lastName }}
        </h2>
      </div>

      <div class="info-grid">
        <!-- Personal Info Card -->
        <div class="info-card">
          <h3 class="card-title">Personal Information</h3>
          <div class="info-item">
            <span class="info-label">Date of Birth:</span>
            <span class="info-value">{{ patient.birthDate }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Home Phone:</span>
            <span class="info-value">{{ formatPhone(patient.homePhone) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Cell Phone:</span>
            <span class="info-value">{{ formatPhone(patient.cellPhone) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Work Phone:</span>
            <span class="info-value">{{ formatPhone(patient.workPhone) }}</span>
          </div>
        </div>

        <!-- Address Info Card -->
        <div class="info-card">
          <h3 class="card-title">Address Information</h3>
          <div class="info-item">
            <span class="info-label">Address:</span>
            <span class="info-value">{{ patient.address1 }} {{ patient.address2 }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">City:</span>
            <span class="info-value">{{ patient.city }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">State:</span>
            <span class="info-value">{{ patient.state }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Zip:</span>
            <span class="info-value">{{ patient.postalCode }}</span>
          </div>
        </div>
      </div>

      <!-- Dependents Card -->
      <div v-if="patient && !patient.isSubAccount" class="dependents-card">
        <h3 class="card-title">Dependents</h3>
        <div v-if="!hasDependents" class="no-dependents">
          <v-icon class="no-deps-icon">mdi-account-details</v-icon>
          <p class="no-deps-text">No dependents for the selected patient.</p>
        </div>
        <div v-else class="dependents-list">
          <div v-for="dep in dependents" :key="dep.FName" class="dependent-item">
            <v-avatar size="36" class="dependent-avatar">
              {{ getInitials(dep.firstName, dep.lastName) }}
            </v-avatar>
            <div class="dependent-info">
              <span class="dependent-name">{{ dep.firstName + ' ' + dep.lastName }}</span>
              <span class="dependent-birthdate">{{ formatDate(dep.birthDate) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import DataService from '../../../_services/DataService';
import Utils from '../../../src/utils.js';

export default {
  name: 'User_Data',
  props: ['userInfo'],
  components: {},
  data() {
    return {
      pageName: 'User Data',
      loading: true,
      patient: null,
      dependents: [],
      hasDependents: false,
    };
  },
  computed: {},
  watch: {
    dependentPage: {
      async handler() {
        await this.getDependentData();
      },
      deep: false,
    },
  },
  methods: {
    getInitials(firstName, lastName) {
      const first = firstName && firstName.length > 0 ? firstName.charAt(0) : '';
      const last = lastName && lastName.length > 0 ? lastName.charAt(0) : '';
      return first + last;
    },
    formatDate(item) {
      if (item != undefined){
        return Utils.formatDate(item);
      }
      else return '-';
    },
    formatPhone(item) {
      if (item != undefined) {
        return Utils.formatPhone(item);
      } else return '-';
    },
    async getDependents() {
      const familyResponse = await DataService.getPatientFamily();
      this.dependents = familyResponse.data.filter((pat) => pat.id !== this.patient.id);

      this.hasDependents = this.dependents.length > 0;
    },
  },
  created() {},
  async mounted() {
    if (this.userInfo) {
      this.patient = this.userInfo;
      this.dependents = this.userInfo.dependents || [];
      this.hasDependents = this.dependents.length > 0;
    } else {
      const patientResp = await DataService.getPatientInfo();
      this.patient = patientResp.data;
      await this.getDependents();
    }
    
    this.loading = false;
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';

// Component-specific color variables
$profile-header-bg: linear-gradient(180deg, #133b54 0%, #1a365d 100%);
$profile-avatar-bg: #b0d35e;
$profile-name-color: white;
$avatar-text-color: white;
$card-bg-color: #ffffff;
$card-border-color: #e2e8f0;
$card-title-color: #2d3748;
$info-label-color: #4a5568;
$info-value-color: #2d3748;
$info-border-color: #f7fafc;
$no-deps-icon-color: #cbd5e0;
$no-deps-text-color: #718096;
$dependent-item-bg: #f8fafc;
$dependent-avatar-bg: #133b54;
$dependent-name-color: #2d3748;
$dependent-birthdate-color: #718096;

.user-data-container {
  padding: 0;
}

.profile-layout {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.profile-header-card {
  background: $profile-header-bg;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  box-shadow: $shadow-md;

  @media (max-width: $breakpoint-mobile) {
    margin-top: 16px;
  }
  
  .avatar-section {
    margin-bottom: 16px;
  }
  
  .profile-avatar {
    background-color: $profile-avatar-bg !important;
    
    .avatar-text {
      color: $avatar-text-color;
      font-size: 32px;
      font-weight: 600;
    }
  }
  
  .profile-name {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
    color: $profile-name-color !important;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  
  @media (max-width: $breakpoint-mobile) {
    grid-template-columns: 1fr;
  }
}

.info-card, .dependents-card {
  background-color: $card-bg-color;
  border-radius: 12px;
  padding: 24px;
  box-shadow: $shadow-md;
  border: 1px solid $card-border-color;
  
  .card-title {
    font-size: 18px;
    font-weight: 600;
    color: $card-title-color;
    margin: 0 0 20px 0;
    font-family: 'Inter', sans-serif;
  }
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid $info-border-color;
  
  &:last-child {
    border-bottom: none;
  }
  
  .info-label {
    font-weight: 500;
    color: $info-label-color;
    font-size: 14px;
  }
  
  .info-value {
    font-weight: 400;
    color: $info-value-color;
    font-size: 14px;
    text-align: right;
  }
}

.dependents-card {
  .no-dependents {
    text-align: center;
    padding: 32px 16px;
    
    .no-deps-icon {
      font-size: 48px;
      color: $no-deps-icon-color;
      margin-bottom: 12px;
    }
    
    .no-deps-text {
      color: $no-deps-text-color;
      margin: 0;
      font-size: 14px;
    }
  }
  
  .dependents-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .dependent-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: $dependent-item-bg;
    border-radius: 8px;
    border: 1px solid $card-border-color;
    
    .dependent-avatar {
      background-color: $dependent-avatar-bg !important;
      color: $avatar-text-color;
      margin-right: 12px;
    }
    
    .dependent-info {
      display: flex;
      flex-direction: column;
    }
    
    .dependent-name {
      font-weight: 500;
      color: $dependent-name-color;
      font-size: 14px;
    }
    
    .dependent-birthdate {
      font-size: 12px;
      color: $dependent-birthdate-color;
      margin-top: 2px;
    }
  }
}
</style>
